
// Base.
import Component from 'vue-class-component'
import Vue from 'vue'
import {State} from 'vuex-class'
import {Watch} from 'vue-property-decorator'

// Used to call service.
import EventsService from '../services/EventsService'
import SchoolsService from '../services/SchoolsService'

// Used Models.
import Event from '../models/event'
import GridHeader from '../models/gridHeader'
import ActionTypes from '../models/constants/ActionTypes'
import Permissions from '../models/constants/Permissions'

// Used Components
import CardTable from '../components/Layout/CardTable.vue'
import DeleteEvents from '../components/Events/DeleteEvents.vue'
import ExportEvents from '../components/Events/ExportEvents.vue'
import CardTitle from '../components/Atomic/CardTitle.vue'
import DatePicker from '../components/Atomic/DatePicker.vue'
import RedButton from '../components/Atomic/RedButton.vue'
import HelpTooltip from '../components/Atomic/HelpTooltip.vue'

import Schools from '@/models/schools'

import moment from 'moment'

interface Filters {
  actionType: string[],
  dateOfIssue: Date[],
  username: string[],
  school_id: number[],
}

interface QueryArgs {
  pageNumber: number,
  pageSize: number,
  sortBy: string,
  sortDesc: boolean,
  school_id: string | null,
  dateOfIssue: string,
  actionType: string,
  information: string,
  username: string,
  status: boolean | undefined,
}

@Component({
  components: {
    CardTable,
    DeleteEvents,
    ExportEvents,
    CardTitle,
    DatePicker,
    RedButton,
    HelpTooltip,
  },
})
export default class Events extends Vue {
  public $refs!: {
    childTable: CardTable,
  }
  @State private user!: any
  // Data properties.
  private events: Event[] = []
  private isPending: boolean = false

  // UI properties.
  private headers: GridHeader[] = []
  private actionTypesValues: string[] = []
  private menu: boolean = false
  private status: Array<{ name, value }> = [{name: 'Réussite', value: true}, {name: 'Echec', value: false}]

  // Filter properties.
  private eventsDates: string[] = []
  private schools: Schools[] = []
  private usernames: string[] = []
  private actions: string[] = []

  private serverItems: number = -1
  private args: QueryArgs = {
    pageNumber: 1,
    pageSize: 25,
    sortBy: '',
    sortDesc: false,
    school_id: null,
    dateOfIssue: '',
    actionType: '',
    information: '',
    username: '',
    status: undefined,
  }
  private footerOptions: { itemsPerPageText, itemsPerPageOptions } = {
    itemsPerPageText: this.$t('EVENT.EventPerPage'),
    itemsPerPageOptions: [25, 50, 100],
  }

  constructor() {
    super()
  }

  public mounted() {
    this.initialize()
  }

  public get parseDate() {
    return this.formatDateToDatePicker(this.args.dateOfIssue, '-')
  }

  public set parseDate(date: any) {
    this.args.dateOfIssue = date
  }

  private get hasAccessToDeleteEvents(): boolean {
    const allowed = [Permissions.DeleteEvents]
    if (
        (this.user.permissions as []).some((permission) =>
            allowed.includes(permission),
        )
    ) {
      return true
    }
    return false
  }

  private formatDateToDatePicker(selectedDate: string, splitCharacter: string) {
    let formattedDate: string | null = null
    if (selectedDate) {
      const [year, month, day] = selectedDate.split(splitCharacter)
      formattedDate = `${day}.${month}.${year}`
    }
    return formattedDate
  }

  private async initialize() {
    await this.getEventsList()
    this.createHeaders()
    this.$refs.childTable.setTableHeight()
    this.actionTypesValues = Object.values(ActionTypes).sort()
  }

  private async updatePageNumber(page: number) {
    this.args.pageNumber = page
    await this.getEventsList()
  }

  private async updatePageSize(pageSize: number) {
    this.args.pageSize = pageSize
    await this.getEventsList()
  }

  private async updateSort(sortBy: string | string[]) {
    this.args.sortBy = Array.isArray(sortBy) ? sortBy[0] : sortBy
  }

  private async updateSortDesc(sortDesc: boolean | boolean[]) {
    this.args.sortDesc = Array.isArray(sortDesc) ? sortDesc[0] : sortDesc
    await this.getEventsList()
  }

  private async getEventsList() {
    try {
      this.isPending = true
      const response: { events, total, filters } = await EventsService.getEvents(this.args)
      this.events = response.events
      this.serverItems = response.total
      this.populateFilterData(response.filters)
      this.events.forEach((event: Event) => {
        let schoolName = 'Inconnu'
        if (event.school_id !== -2) {
          const schoolObject = this.schools.find(
              (school) => school.etablissement_id === event.school_id,
          )
          if (schoolObject !== undefined) {
            schoolName = schoolObject.nom
          }
        }
        event.school_id = event.school_id === -2 ? 'N/A' : schoolName
        event.username = event.username === '' ? 'N/A' : event.username
      })
    } catch (error: any) {
      console.log(error)
    }
    this.isPending = false
  }

  private allowedDates(val: string) {
    return this.eventsDates.indexOf(val) !== -1
  }

  private populateSchools(ids: number[]) {
    this.schools = this.user.schools.filter((school) => {
      return ids.includes(school.etablissement_id)
    })
    if (ids.includes(-2)) {
      this.schools.push({nom: 'Inconnu', etablissement_id: -2})
    }
  }

  private populateUsernames(usernames: string[]) {
    usernames.forEach((username) => {
      if (!this.usernames.includes(username)) {
        this.usernames.push(username)
      }
    })
  }

  private populateActions(actions: string[]) {
    actions.forEach((action) => {
      if (!this.actions.includes(action)) {
        this.actions.push(action)
      }
    })
  }

  private populateEventsDates(dates: Date[]) {
    dates.forEach((date) => {
      const eventDate = moment(date).format('YYYY-MM-DD')
      if (!this.eventsDates.includes(eventDate)) {
        this.eventsDates.push(eventDate)
      }
    })
  }

  private get hasEventsToFilter() {
    return this.events.length > 0
  }

  private populateFilterData(filters: Filters) {
    this.populateSchools(filters.school_id)
    this.populateUsernames(filters.username)
    this.populateActions(filters.actionType)
    this.populateEventsDates(filters.dateOfIssue)
  }

  private clearFiltersSelection() {
    this.schools = [...this.user.schools]
    this.args.pageNumber = 1
    this.args.school_id = null
    this.args.dateOfIssue = ''
    this.args.actionType = ''
    this.args.information = ''
    this.args.username = ''
    this.args.status = undefined
  }

  private clearFiltersData() {
    this.schools = [...this.user.schools]
    this.usernames = []
    this.actions = []
    this.eventsDates = []
  }

  private async resetFilterDialog() {
    this.clearFiltersSelection()
    this.clearFiltersData()
    await this.getEventsList()
  }

  private createHeaders() {
    this.headers.push(
        new GridHeader(this.$t('EVENT.School').toString(), 'school_id'),
    )
    this.headers.push(
        new GridHeader(this.$t('EVENT.UsernameLabel').toString(), 'username'),
    )
    this.headers.push(
        new GridHeader(this.$t('EVENT.DateLabel').toString(), 'dateOfIssue'),
    )
    this.headers.push(
        new GridHeader(this.$t('EVENT.ActionTypeLabel').toString(), 'actionType'),
    )
    this.headers.push(
        new GridHeader(
            this.$t('EVENT.InformationLabel').toString(),
            'information',
        ),
    )
    this.headers.push(
        new GridHeader(this.$t('EVENT.Status').toString(), 'status'),
    )
  }

  private get filterSelected() {
    const filters = ['school_id', 'username', 'dateOfIssue', 'actionType', 'information', 'status']
    return filters.filter((filter) => this.$data['args'][filter] !== '' && this.$data['args'][filter] !== null && this.$data['args'][filter] !== undefined)
  }

  private resetFilter(item) {
    this.$data['args'][item] = ''
  }

}
